/**
 * 28 spec タイポグラフィ階層 + アイコンサイズ + 重み
 *
 * 一次情報:
 *  - docs/design-system/03_ui_implementation_spec.md §3.2 (type scale)
 *  - docs/design-system/03_ui_implementation_spec.md §8.1 (icon size)
 *  - docs/design-system/03_ui_implementation_spec.md §9 (Tailwind config)
 *
 * Notion ヘディングは line-height 1.2-1.35 のタイト設計が「monumental」を作る。
 * 詰まって見える時は line-height ではなく heading 周辺の縦リズム (mb/mt) を広げる。
 */

/**
 * 28 spec §3.1 準拠のフルスタック (Inter は Notion 実装が採用しているのと同じ neo-grotesque):
 *  `ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
 *   "Segoe UI", Inter, "Hiragino Sans", "Yu Gothic UI", "Yu Gothic", "Noto Sans JP", sans-serif`
 *
 * web 検索で確認: Notion は Inter を primary UI typeface として採用 (designyourway / aeanet 等の
 * 複数 source 一致)。 `index.html` の Google Fonts `<link>` で配信。
 */
export const FONT_FAMILY_SANS =
  'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, "Hiragino Sans", "Yu Gothic UI", "Yu Gothic", "Noto Sans JP", sans-serif'

export const FONT_FAMILY_SERIF =
  'ui-serif, Georgia, "Hiragino Mincho ProN", serif'

export const FONT_FAMILY_MONO =
  'ui-monospace, "SF Mono", Menlo, Consolas, monospace'

/**
 * 28 spec §3.2 全 10 段階のタイプスケール
 * (title-page → h1 → h2 → h3 → h4 → body → body-sm → label → tag → micro)
 *
 * 用途指針:
 *  - titlePage: ページタイトル (40px)
 *  - h1-h4    : ページ内見出し
 *  - body     : 本文デフォルト (16px)
 *  - bodySm   : サイドバー項目・キャプション (14px) ← MUI variant `body2`
 *  - label    : UI ラベル・ボタン文字 (13px、Notion で最頻出) ← MUI variant `button`
 *  - tag      : バッジ・チップ (12px) ← MUI variant `caption`
 *  - micro    : ショートカット表示・二次メタ情報 (11px、Citation 等)
 */
export const typeScale = {
  // 28 spec §3.3: 見出し H1/H2 は letter-spacing -0.01em (わずかにタイト)
  titlePage: {
    fontSize: 40,
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  h1: {
    fontSize: 30,
    lineHeight: 1.3,
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  h2: {
    fontSize: 24,
    lineHeight: 1.35,
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },
  h3: { fontSize: 20, lineHeight: 1.4, fontWeight: 600 },
  h4: { fontSize: 16, lineHeight: 1.5, fontWeight: 600 },
  body: { fontSize: 16, lineHeight: 1.5, fontWeight: 400 },
  bodySm: { fontSize: 14, lineHeight: 1.45, fontWeight: 400 },
  label: { fontSize: 13, lineHeight: 1.4, fontWeight: 500 },
  tag: { fontSize: 12, lineHeight: 1.3, fontWeight: 500 },
  micro: { fontSize: 11, lineHeight: 1.3, fontWeight: 500 },
} as const

/**
 * UI で直接 fontSize 指定する時のセマンティック定数
 * (MUI Typography variant を超えたインライン指定向け)
 */
export const UI_FONT_SIZE = {
  micro: typeScale.micro.fontSize, // 11
  tag: typeScale.tag.fontSize, // 12
  label: typeScale.label.fontSize, // 13
  bodySm: typeScale.bodySm.fontSize, // 14
  body: typeScale.body.fontSize, // 16
} as const

/**
 * 文字サイズモード対応のインライン fontSize (ADR-0019).
 * theme.fontScale (= createAppTheme(fontScale) が埋める倍率) を乗算した px を返す
 * theme callback. scale=1 では Math.round(px * 1) === px で静的指定と完全等価.
 *
 * 使い方: sx={{ fontSize: scaledFontSize('bodySm') }}
 * 禁則: styled(...) へ静的オブジェクトで渡すと callback が評価されない.
 *       styled(El)(({ theme }) => ({ fontSize: scaledFontSize('label')(theme) })) の形にする.
 */
export const scaledFontSize =
  (tier: keyof typeof UI_FONT_SIZE) =>
  (theme: { fontScale?: number }): number =>
    Math.round(UI_FONT_SIZE[tier] * (theme.fontScale ?? 1))

/**
 * 28 spec §8.1 アイコンサイズ (Lucide stroke 1.5px)
 *  - UI: 14 / 16 / 18 / 20
 *  - Decorative: 22 / 24 (枠外、必要時のみ別途指定)
 *
 * 用途指針:
 *  - sm (14): 二次情報の verify mark, sidebar 内 secondary action
 *  - md (16): body 内 action icon (Copy/Expand/Search)
 *  - lg (18): primary navigation (Sidebar 5 icon, ChevronRight, Mic)
 *  - xl (20): emphasis 用 (大きめ表現)
 */
export const ICON_SIZE = {
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
} as const

/**
 * フォント重み — 28 spec §3.2 で 400/500/600/700 が登場
 *  - regular  : 本文
 *  - medium   : ラベル・ボタン (label/tag/micro の標準)
 *  - semibold : セクション見出し (h2-h4) ← MUI fontWeightMedium と異なる、Notion で多用
 *  - bold     : ページタイトル・h1
 */
export const FONT_WEIGHT = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const

// ========================================================================
// log/33 P3 cleanup: legacy typography.legacy.ts merge
// ------------------------------------------------------------------------
// 機械的文字列 (ID, URL, トークン) を等幅で表示する用途.
// Inter の等幅特性 + monospace fallback.
// ========================================================================
export const FONT_MONO = {
  fontFamily: "'Inter', monospace",
} as const

// 等幅数字 (リスト中の番号を縦に揃える). Inter の tnum 機能を使用.
export const FONT_TABULAR = {
  fontFeatureSettings: "'tnum'",
} as const
